import React, { useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import { Modal, theme } from 'antd'
import styled from 'styled-components'
import { useAuth } from '@/libs/context/auth'
import SubscriptionPicker from './subscription-picker'

const { useToken } = theme

const StyledModal = styled(Modal)`
  .ant-modal-content {
    border-radius: ${({ token }) => token.borderRadiusLG}px;
    box-shadow: ${({ token }) => token.boxShadowTertiary};
  }

  .ant-modal-header {
    border-bottom: 1px solid ${({ token }) => token.colorBorderSecondary};
    padding: ${({ token }) => token.paddingLG}px;
  }

  .ant-modal-body {
    padding: ${({ token }) => token.paddingLG}px;
  }

  .ant-modal-footer {
    border-top: 1px solid ${({ token }) => token.colorBorderSecondary};
    padding: ${({ token }) => token.paddingLG}px;
  }
`

/**
 * SubscriptionUpgradeModal component for handling subscription upgrades
 * @param {Object} props - Component props
 * @param {boolean} props.visible - Whether the modal is visible
 * @param {Function} props.setIsVisible - Function to control modal visibility
 * @returns {JSX.Element} Rendered subscription upgrade modal
 */
const SubscriptionUpgradeModal = ({ visible = true, setIsVisible = () => {} }) => {
  const { token } = useToken()
  const { logout } = useAuth()
  const [isSubsModalOpen, setIsSubsModalOpen] = useState(visible)

  // Update internal state when prop changes
  React.useEffect(() => {
    setIsSubsModalOpen(visible)
  }, [visible])

  /**
   * Handle modal cancel action
   * Logs out the user when they cancel the subscription upgrade
   */
  const handleCancel = useCallback(() => {
    try {
      logout()
    } catch (error) {
      console.error('Error during logout:', error)
    }
  }, [logout])

  /**
   * Handle subscription purchase completion
   * Closes the modal and updates parent state
   */
  const handleTierPurchased = useCallback(() => {
    setIsSubsModalOpen(false)
    if (setIsVisible) {
      setIsVisible(false)
    }
  }, [setIsVisible])

  return (
    <StyledModal
      open={isSubsModalOpen}
      footer={null}
      closable={true}
      mask={{ closable: false }}
      keyboard={false}
      onCancel={handleCancel}
      width={800}
      centered
      token={token}
      destroyOnHidden
    >
      <SubscriptionPicker onTierPurchased={handleTierPurchased} />
    </StyledModal>
  )
}

SubscriptionUpgradeModal.propTypes = {
  visible: PropTypes.bool,
  setIsVisible: PropTypes.func,
}

export default SubscriptionUpgradeModal
